import { useNavigate } from "react-router-dom";
import { useCommon } from "../context/CommonContext";
import api from "../api/Api";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import { useLanguage } from "../context/LanguageProvider";

/**
 * ExplicitQuestions
 * ------------------
 * Purpose:
 *  This screen renders the "explicit" phase of the survey: 
 *  a set of direct questions to be answered after an "aroma memory" is played.
 * 
 * Flow:
 *  - Fetches question set for the current survey from API (if needed).
 *  - Handles a "no data" scenario from the API (404 response) by skipping ahead.
 *  - Tracks which answer options the participant has selected for each question.
 *  - Waits until all questions are answered (or none are present) before enabling "Press Space to Continue".
 *  - On Space bar press, saves responses to global results, navigates to the next aroma memory or demographics step.
 *   - Pagination: Shows 4 questions per page and auto-advances when all are answered, until last page.
 */
const ExplicitQuestions = () => {
    // Allows programmatic page navigation
    const navigate = useNavigate();

    // Destructure shared state & setters from the global CommonContext
    const { 
        currentMemoryIndex, // ref pointer to which memory in the sequence we're on
        memories,           // list of aroma/memory items
        explicitQuestions,  // global array holding explicit questions (once loaded)
        setExplicitQuestions, // setter to update above
        surveyId,            // id of current running survey
        explicitResults,      // ref array where explicit question results are accumulated
        intermissionText,      // Prompt for Survey Intermission message
        intermissionNumber,     // Samples between intermissions
        intermissionTime,       // Seconds in Intermisson 
        sampleMode,
        continueButtonText,
        nextButtonText,
    } = useCommon();

    // Translation function from LanguageProvider for all static text
    const { translation } = useLanguage();

    // Local state: whether we have answered all questions and are allowed to continue
    const [canContinue, setCanContinue] = useState(false);

    // Local state: an object mapping { questionIndex: selectedOptionString }
    const [selectedOptions, setSelectedOptions] = useState({});

    // For Intermissions
    const [isIntermission, setIsIntermission] = useState(false);
    const [timeLeft, setTimeLeft] = useState(intermissionTime);

    // State to track the current page for paginated questions
    const [currentPage, setCurrentPage] = useState(0);

    /**
     * Determine whether we should fetch explicit questions from API:
     * - If explicitQuestions array is empty
     * - OR it doesn't contain a special "no_data" marker object 
     *   (marker is used to skip future fetch attempts if API already told us there's no data)
     */
    const shouldFetch = explicitQuestions.length === 0 || 
                        !explicitQuestions.some(q => q.isNoDataMarker);

    /**
     * useQuery hook from React Query handles async fetching and caching.
     * - queryKey: uniquely identifies this request in cache.
     * - queryFn: the actual async function to fetch questions for this survey id.
     * - enabled: only run if shouldFetch is true.
     * - retry: false to skip auto-retry on error (especially on 404, which is expected in some flows).
     */
    const { data, status, error } = useQuery({
        queryKey: ['explicitQuestions', surveyId],
        queryFn: () => api.fetchExplicitQuestions(surveyId),
        enabled: shouldFetch,
        retry: false
    });

    // Filter out the "no data" marker for display purposes
    const displayQuestions = explicitQuestions.filter(q => !q.isNoDataMarker);

    /**
     * Effect: when data successfully arrives from API, populate context's explicitQuestions.
     * Runs whenever `status` or `data` changes.
     */
    useEffect(() => {
        if (status === "success" && data) {
            setExplicitQuestions(data);
        }
    }, [status, data, setExplicitQuestions]);

    // Effect: handle countdown when in intermission mode
    useEffect(() => {
        let timer;
        if (isIntermission && timeLeft > 0) {
            timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [isIntermission, timeLeft]);

    /**
     * Effect: handle a specific "no data" case (404 error from API).
     * - Sets a marker in context so we won't call API again for this condition.
     * - Navigates ahead to next memory OR to demographics if this was last memory.
     */
    useEffect(() => {
        if (status === "error" && error && error.message === "Error: 404") {
            // Marker to skip future fetch attempts
            setExplicitQuestions([{ isNoDataMarker: true }]);
            
            // Check if there are more memories to process
            if (currentMemoryIndex.current < memories.length - 1) {
                currentMemoryIndex.current += 1;
                 if(sampleMode!=="VAS" && intermissionNumber.current > 0 && intermissionTime.current > 0 && currentMemoryIndex.current % intermissionNumber.current === 0){
                    // Trigger intermission mode instead of immediate navigation
                    setTimeLeft(intermissionTime.current);
                    setIsIntermission(true);
                    return; // stop here so navigation happens after intermission
                }
                navigate("/playingAroma");
            } else {
                navigate("/Demographics");
            }
        }
    }, [status, error, navigate, currentMemoryIndex, memories, setExplicitQuestions]);

    /**
     * Effect: enable "continue" conditionally.
     * - If there are no questions, participants can continue immediately.
     * - If there are questions, all must be answered before enabling.
     */
    useEffect(() => {
        if (displayQuestions.length === 0) {
            setCanContinue(true);
        } else {
            const allAnswered = Object.keys(selectedOptions).length === displayQuestions.length;
            setCanContinue(allAnswered);
        }
    }, [selectedOptions, displayQuestions]);

    /**
     * Handle Space bar press to continue.
     * Only active if `canContinue` is true.
     * Saves answers to global results before navigating:
     *  [question_id, memory_value, selectedOption, question_type]
     */
    const handleKeyPress = () => {
        if (isIntermission) return; // Ignore all keypresses in intermission mode
        if (canContinue) {
            // Append each answered question into the results array
            displayQuestions.forEach((question, index) => {
                if (selectedOptions[index]) {
                    explicitResults.current.push([
                        question.question_id,
                        memories[currentMemoryIndex.current],
                        selectedOptions[index],
                        question.question_type,
                    ]);
                }
            });

            // Next step depends on whether we have more memories to process
            if (currentMemoryIndex.current < memories.length - 1) {
                currentMemoryIndex.current += 1;
                if(sampleMode!=="VAS" && intermissionNumber.current > 0 && intermissionTime.current > 0 && currentMemoryIndex.current % intermissionNumber.current === 0){
                    // Trigger intermission mode instead of immediate navigation
                    setTimeLeft(intermissionTime.current);
                    setIsIntermission(true);
                    return; // stop here so navigation happens after intermission
                }
                navigate("/playingAroma");
            } else {
                navigate("/Demographics");
            }
        }
    };

    /**
     * Effect: bind global keydown listener for Space to `handleKeyPress`.
     * Clean up when dependencies change or component unmounts.
     * Dependency array includes canContinue so the handler only allows navigation when it's true.
     */
    // useEffect(() => {
    //     if (!isIntermission) {
    //         window.addEventListener("keydown", handleKeyPress);
    //         return () => window.removeEventListener("keydown", handleKeyPress);
    //     }
    // }, [canContinue, isIntermission]);

    // Calculate pagination variables
    const questionsPerPage = 4;
    const startIndex = currentPage * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    const currentQuestions = displayQuestions.slice(startIndex, endIndex);
    // Calculate total pages for pagination
    const totalPages = Math.ceil(displayQuestions.length / questionsPerPage);
    // Check if all questions on the current page are answered to enable Next button
    const isCurrentPageAnswered = currentQuestions.every((_, idx) => selectedOptions.hasOwnProperty(startIndex + idx));

    // // Auto-advance effect
    // useEffect(() => {
    //     if (isCurrentPageAnswered) {
    //         if (currentPage < totalPages - 1) {
    //             setTimeout(() => setCurrentPage(prev => prev + 1), 300);
    //         } else {
    //             setCanContinue(true);
    //         }
    //     }
    // }, [isCurrentPageAnswered, currentPage, totalPages]);

    /**
     * Error handling for non-404 cases:
     * - If message starts with "Error:" → Show Error + message
     * - Otherwise, assume a network error and show localized "Network error" message.
     */
    if (status === "error" && error && error.message !== "Error: 404") {
        if (error.message.startsWith("Error:")) {
            return <div>{translation("Error")} {error.message} </div>;
        }
        return <div>{translation("Network_error")}</div>;
    }

    /**
     * While fetching data (and only when we expect data), show loading component.
     */
    if (status === "pending" && shouldFetch) {
        return <Loading />;
    }

    /**
     * Handles when participant clicks on an answer option for a question.
     * Updates selectedOptions for that question index, preserving other answers.
     */
    const handleOptionClick = (questionIndex, option) => {
        setSelectedOptions((prev) => ({ ...prev, [questionIndex]: option }));
    };

    if (isIntermission) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
                <h2 className="text-2xl font-bold mb-4">{intermissionText.current}</h2>
                
                <div style={{ minHeight: "2rem" }} className="flex items-center">
                    <p className="text-xl">{timeLeft > 0 ? timeLeft : "0"}</p>
                </div>

                <button
                    onClick={() => navigate("/playingAroma")}
                    className="px-6 py-2 bg-black text-white text-xl font-semibold rounded-lg shadow hover:bg-gray-800 transition duration-200 mt-8"
                    style={{
                        visibility: timeLeft === 0 ? "visible" : "hidden",
                    }}
                    disabled={timeLeft > 0}
                >
                    {continueButtonText.current}
                </button>
            </div>
        );
    }


    /**
     * Render:
     * - List all displayQuestions with their prompt & answer options.
     * - Apply selected state styling to chosen option.
     * - If canContinue is true, show instruction to press Space to continue.
     */
    return (
        // Updated main container to flex-col without justify-center or relative to allow proper space allocation
        <div className="flex flex-col items-center min-h-screen bg-gray-100">
            {/* Questions container takes remaining space, scrollable if content overflows */}
            <div className="flex-1 w-full flex flex-col items-center justify-center overflow-y-auto py-2">
                {currentQuestions.map((question, idx) => (
                    <div key={idx} className="mb-1">
                        <h4 className="text-2xl font-semibold mt-1">{question.question_prompt}</h4>
                        <div>
                            {question.question_options.map((option, index) => (
                                <button 
                                    key={index} 
                                    onClick={() => handleOptionClick(startIndex + idx, option)} 
                                    className={`m-4 p-2 px-5 text-black text-base font-semibold border-2 border-black rounded-md hover:scale-110 ${selectedOptions[startIndex + idx] === option ? 'bg-black text-white' : ''}`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Navigation buttons fixed at bottom above continue, with positions fixed: previous left, next right */}
            {/* {totalPages > 1 && (
                <div className="w-full max-w-md flex justify-between py-4">
                    <div>
                        {currentPage > 0 && (
                            <button 
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="px-6 py-2 bg-gray-800 text-white font-semibold rounded-md hover:bg-gray-900"
                            >
                                Previous
                            </button>
                        )}
                    </div>
                    <div>
                        {currentPage < totalPages - 1 && (
                            <button 
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                disabled={!isCurrentPageAnswered}
                                className={`px-6 py-2 font-semibold rounded-md ${isCurrentPageAnswered ? 'bg-gray-800 text-white hover:bg-gray-900' : 'bg-gray-400 text-gray-200 cursor-not-allowed'}`}
                            >
                                Next
                            </button>
                        )}
                    </div>
                </div>
            )} */}
            {/* Render next button */}
            {totalPages > 1 && currentPage < totalPages - 1 && (
            <div className="w-full max-w-md flex justify-end py-4">
                <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!isCurrentPageAnswered}
                className={`px-6 py-2 font-semibold rounded-md ${
                    isCurrentPageAnswered
                    ? 'bg-gray-800 text-white hover:bg-gray-900'
                    : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                }`}
                >
                {nextButtonText.current}
                </button>
            </div>
            )}

            {/* Continue message at the very bottom, no absolute positioning to avoid overlay */}
            <button className={`font-semibold rounded-md bg-gray-800 text-white hover:bg-gray-900 px-6 py-2 mb-4 ${canContinue ? 'visible' : 'invisible'}`}
                    onClick={handleKeyPress}
                    disabled={!canContinue}>
                {continueButtonText.current}
            </button>
        </div>
    );
};

export default ExplicitQuestions;